// A gesture's uncommitted loop edits per `live.modulators` key; Redux never sees a drag frame, only the gesture's end.
import { useSyncExternalStore } from 'react'
import { conformEntry, defOf } from './model'

const drafts = new Map()
const listeners = new Map()
const dirty = new Set()
let frame = 0
let version = 0

// Subscribers hear at most once a frame, however fast the pointer.
const flush = () => {
	frame = 0
	for (const path of dirty) for (const fn of listeners.get(path) ?? []) fn()
	dirty.clear()
}

const changed = path => {
	version++
	dirty.add(path)
	if (!frame) frame = requestAnimationFrame(flush)
}

/** Merge a drag frame's `patch` into `key`'s draft. */
export const draftLoop = (key, patch) => {
	drafts.set(key, { ...drafts.get(key), ...patch })
	changed(key)
}

/** End `key`'s draft, returning what it held, for the gesture's one Redux write. */
export const takeDraft = key => {
	const draft = drafts.get(key)
	if (!draft) return undefined
	drafts.delete(key)
	changed(key)
	return draft
}

/** `section` with the drafts of `keys` over it, each conformed as the reducer would; the same object when none applies. */
const overlay = (section, keys, objects) => {
	let out = null
	for (const key of keys) {
		const draft = drafts.get(key)
		if (!draft || !section?.[key]) continue
		const def = conformEntry(key, { ...section[key], ...draft }, objects)
		if (def) (out ??= { ...section })[key] = def
	}
	return out ?? section
}

/** The def on `path` (as `defOf` resolves it) with its own and its driver's drafts over it. */
export const drafted = (path, section, objects) => {
	const src = section?.[path]?.src
	return defOf(overlay(section, src ? [path, src] : [path], objects), path)
}

let memo = { state: null, version: -1, out: null }

/** Store state with every draft laid over `live.modulators`; the same object until either changes. */
export const withDrafts = state => {
	if (!drafts.size || !state.live?.modulators) return state
	if (memo.state === state && memo.version === version) return memo.out
	const { live } = state
	const modulators = overlay(live.modulators, drafts.keys(), live.objects)
	memo = { state, version, out: { ...state, live: { ...live, modulators } } }
	return memo.out
}

const subscribe = path => fn => {
	if (!listeners.has(path)) listeners.set(path, new Set())
	listeners.get(path).add(fn)
	return () => listeners.get(path).delete(fn)
}

const subscribers = new Map()

/** `key`'s draft patch, re-rendering once a frame while a gesture moves it. */
export const useDraft = key => {
	if (!subscribers.has(key)) subscribers.set(key, subscribe(key))
	return useSyncExternalStore(subscribers.get(key), () => drafts.get(key))
}
